import {
  NODE_BR,
  NODE_CODEBLOCK,
  NODE_EMOJI,
  NODE_HEADING,
  NODE_HR,
  NODE_IMAGE,
  NODE_LI,
  NODE_OL,
  NODE_PARAGRAPH,
  NODE_QUOTE,
  NODE_UL,
  StoryblokRichtext,
  StoryblokRichtextContent
} from "storyblok-rich-text-react-renderer"

const renderNode = (node: StoryblokRichtextContent, addNewlines: boolean) => {
  if (node.type === "text") {
    return node.text
  } else if (
    [NODE_PARAGRAPH, NODE_HEADING, NODE_CODEBLOCK, NODE_QUOTE, NODE_OL, NODE_UL, NODE_LI, NODE_HR, NODE_BR].includes(
      node.type
    )
  ) {
    return node.content?.length ? `${renderNodes(node.content, addNewlines)}${addNewlines ? "\n\n" : " "}` : ""
  }

  return null
}

const renderNodes = (nodes: StoryblokRichtext["content"], addNewlines: boolean): string =>
  nodes
    .map(node => renderNode(node, addNewlines))
    .filter(node => node !== null)
    .join("")
    .replace(/[^\S\r\n]{2,}/g, " ")

export interface GetPlainTextOptions {
  addNewlines?: boolean
}

export const getPlainText = (richtext: StoryblokRichtext, { addNewlines }: GetPlainTextOptions = {}): string => {
  if (!richtext?.content?.length) {
    return ""
  }

  const text = renderNodes(richtext.content, addNewlines !== undefined ? addNewlines : true)

  return text
}

/**
 * Structural, not nominal: Storyblok's generated `RichtextStoryblok` types `type` as `string` while
 * the renderer's `StoryblokRichtext` narrows it to `"doc"`, so neither accepts the other. This
 * function only reads `type`, `text` and `content`, so it asks for exactly that and both fit.
 */
type RichtextNodeLike = { type?: string; text?: string; content?: RichtextNodeLike[] }
type RichtextLike = { content?: RichtextNodeLike[] }

/**
 * Nodes that render something of their own without carrying text. Without them a document holding
 * only an image, a rule or an embedded blok would be judged empty.
 */
const STANDALONE_NODES = new Set<string>([NODE_IMAGE, NODE_HR, NODE_EMOJI, "blok"])

/**
 * Whether a richtext field actually renders anything.
 *
 * Checking `content?.length` is not enough. Storyblok keeps a document once the field has been
 * opened in the editor, so all of these are structurally non-empty while rendering nothing:
 *
 *   cleared in the editor   { type: "doc", content: [{ type: "paragraph" }] }
 *   typed and deleted       paragraph -> [{ type: "text", text: "" }]
 *   whitespace left behind  paragraph -> [{ type: "text", text: "   " }]
 *   bullet with no text     bullet_list -> list_item -> paragraph (no content)
 *
 * A node counts only when it has non-whitespace text, or when it is one that renders on its own.
 * The walk is recursive because the text can sit at any depth — a list item nests it three levels
 * down — so a fixed one-level check reports whichever depth it happens to stop at.
 */
export const hasRichtextContent = (richtext?: RichtextLike | null): boolean => {
  const anyVisible = (nodes?: RichtextNodeLike[]): boolean =>
    (nodes ?? []).some(node =>
      node.type === "text"
        ? Boolean(node.text?.trim())
        : STANDALONE_NODES.has(node.type ?? "") || anyVisible(node.content)
    )

  return anyVisible(richtext?.content)
}
